import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';

// ============================================================================
// TYPES
// ============================================================================

export interface UserOption {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface RoleConfig {
  icon: string;
  color: string;
  label: string;
}

// ============================================================================
// USER SEARCH SELECT COMPONENT
// ============================================================================

interface UserSearchSelectProps {
  users: UserOption[];
  selectedId: string | null;
  onChange: (userId: string | null) => void;
  excludeIds?: string[];
  roleConfig: Record<string, RoleConfig>;
  placeholder?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  className?: string;
}

export function UserSearchSelect({
  users,
  selectedId,
  onChange,
  excludeIds = [],
  roleConfig,
  placeholder = 'Search by name or email...',
  allowEmpty = true,
  emptyLabel = 'No Manager (Top Level)',
  className = '',
}: UserSearchSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter users based on search and exclusions
  const filteredUsers = users.filter((u) => {
    if (excludeIds.includes(u.id)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term)
    );
  });

  // Limit to 10 results for performance
  const displayUsers = filteredUsers.slice(0, 10);
  const hasMore = filteredUsers.length > 10;

  // Build flat list of selectable options for keyboard navigation
  // Index 0 = empty option (if allowEmpty), then user options follow
  const optionIds: (string | null)[] = [];
  if (allowEmpty) optionIds.push(null);
  displayUsers.forEach((u) => optionIds.push(u.id));

  // Get selected user for display
  const selectedUser = users.find((u) => u.id === selectedId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opening and reset highlight
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-option]');
      const item = items[highlightedIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSelect = (userId: string | null) => {
    onChange(userId);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < optionIds.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < optionIds.length) {
          handleSelect(optionIds[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {/* Selected Value / Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'w-full px-3 py-2 border border-gray-300 rounded-lg text-left text-sm',
          'bg-white hover:border-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none',
          'flex items-center justify-between gap-2'
        )}
      >
        {selectedUser ? (
          <div className="flex items-center gap-2 min-w-0">
            <span className="flex-shrink-0">{roleConfig[selectedUser.role]?.icon || '👤'}</span>
            <span className="truncate">{selectedUser.name}</span>
            <span className="text-xs text-gray-400 flex-shrink-0">
              ({roleConfig[selectedUser.role]?.label || 'IC'})
            </span>
          </div>
        ) : (
          <span className="text-gray-500">{emptyLabel}</span>
        )}
        <span className="text-gray-400 flex-shrink-0">{isOpen ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Options List */}
          <div ref={listRef} className="max-h-60 overflow-y-auto">
            {/* Empty Option */}
            {allowEmpty && (
              <button
                type="button"
                data-option
                onClick={() => handleSelect(null)}
                className={clsx(
                  'w-full px-3 py-2 text-left text-sm flex items-center gap-2',
                  highlightedIndex === 0
                    ? 'bg-primary-100 text-primary-900'
                    : selectedId === null
                    ? 'bg-primary-50'
                    : 'hover:bg-gray-50'
                )}
              >
                <span className="text-gray-400">—</span>
                <span className="text-gray-600">{emptyLabel}</span>
              </button>
            )}

            {/* User Options */}
            {displayUsers.length > 0 ? (
              displayUsers.map((user, idx) => {
                const config = roleConfig[user.role] || { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'IC' };
                const optIdx = allowEmpty ? idx + 1 : idx;
                return (
                  <button
                    key={user.id}
                    type="button"
                    data-option
                    onClick={() => handleSelect(user.id)}
                    className={clsx(
                      'w-full px-3 py-2 text-left text-sm flex items-center gap-2',
                      highlightedIndex === optIdx
                        ? 'bg-primary-100 text-primary-900'
                        : selectedId === user.id
                        ? 'bg-primary-50'
                        : 'hover:bg-gray-50'
                    )}
                  >
                    <span className="flex-shrink-0">{config.icon}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 truncate">{user.name}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                    <span className={clsx('text-xs px-2 py-0.5 rounded-full flex-shrink-0', config.color)}>
                      {config.label}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                {searchTerm ? 'No users found' : 'No users available'}
              </div>
            )}

            {/* More indicator */}
            {hasMore && (
              <div className="px-3 py-2 text-center text-xs text-gray-400 border-t border-gray-100">
                {filteredUsers.length - 10} more results. Refine your search.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MOVE MANAGER DIALOG COMPONENT
// ============================================================================

export type MoveOption = 'move_team' | 'reassign_to_old' | 'leave_unassigned';

interface MoveManagerDialogProps {
  employee: UserOption;
  directReports: UserOption[];
  oldManager: UserOption | null;
  newManager: UserOption | null;
  roleConfig: Record<string, RoleConfig>;
  onConfirm: (option: MoveOption) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function MoveManagerDialog({
  employee,
  directReports,
  oldManager,
  newManager,
  roleConfig,
  onConfirm,
  onCancel,
  isLoading = false,
}: MoveManagerDialogProps) {
  const [selectedOption, setSelectedOption] = useState<MoveOption>('reassign_to_old');

  const employeeConfig = roleConfig[employee.role] || { icon: '👤', label: 'IC' };
  const reportCount = directReports.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Moving {employee.name}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {employeeConfig.icon} {employee.name} has {reportCount} direct report{reportCount !== 1 ? 's' : ''}.
            What should happen to them?
          </p>
        </div>

        {/* Options */}
        <div className="p-6 space-y-3">
          {/* Option 1: Move entire team */}
          <label
            className={clsx(
              'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors',
              selectedOption === 'move_team'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <input
              type="radio"
              name="moveOption"
              value="move_team"
              checked={selectedOption === 'move_team'}
              onChange={() => setSelectedOption('move_team')}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Move entire team</div>
              <div className="text-sm text-gray-500 mt-1">
                {employee.name} and {reportCount} report{reportCount !== 1 ? 's' : ''} will now be under{' '}
                <strong>{newManager?.name || 'no manager'}</strong>
              </div>
            </div>
          </label>

          {/* Option 2: Move only manager (Recommended) */}
          <label
            className={clsx(
              'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors',
              selectedOption === 'reassign_to_old'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <input
              type="radio"
              name="moveOption"
              value="reassign_to_old"
              checked={selectedOption === 'reassign_to_old'}
              onChange={() => setSelectedOption('reassign_to_old')}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">
                Move only {employee.name}
                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  Recommended
                </span>
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {reportCount} report{reportCount !== 1 ? 's' : ''} will be reassigned to{' '}
                <strong>{oldManager?.name || 'no manager (top level)'}</strong>
              </div>
            </div>
          </label>

          {/* Option 3: Leave unassigned */}
          <label
            className={clsx(
              'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors',
              selectedOption === 'leave_unassigned'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <input
              type="radio"
              name="moveOption"
              value="leave_unassigned"
              checked={selectedOption === 'leave_unassigned'}
              onChange={() => setSelectedOption('leave_unassigned')}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Move only {employee.name}, leave reports unassigned</div>
              <div className="text-sm text-gray-500 mt-1">
                {reportCount} report{reportCount !== 1 ? 's' : ''} will have no manager
              </div>
              <div className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <span>⚠️</span> Requires follow-up to reassign employees
              </div>
            </div>
          </label>

          {/* Direct Reports Preview */}
          {reportCount > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-sm font-medium text-gray-700 mb-2">Affected employees:</div>
              <div className="flex flex-wrap gap-2">
                {directReports.slice(0, 5).map((report) => {
                  const config = roleConfig[report.role] || { icon: '👤', color: 'bg-gray-100 text-gray-800' };
                  return (
                    <span
                      key={report.id}
                      className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs', config.color)}
                    >
                      {config.icon} {report.name}
                    </span>
                  );
                })}
                {reportCount > 5 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                    +{reportCount - 5} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selectedOption)}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isLoading ? 'Moving...' : 'Confirm Move'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// USER MULTI-SELECT COMPONENT
// ============================================================================

interface UserMultiSelectProps {
  users: UserOption[];
  selectedIds: string[];
  onChange: (userIds: string[]) => void;
  excludeIds?: string[];
  roleConfig: Record<string, RoleConfig>;
  placeholder?: string;
  className?: string;
}

export function UserMultiSelect({
  users,
  selectedIds,
  onChange,
  excludeIds = [],
  roleConfig,
  placeholder = 'Search to add owner...',
  className = '',
}: UserMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Selected user objects
  const selectedUsers = selectedIds
    .map((id) => users.find((u) => u.id === id))
    .filter(Boolean) as UserOption[];

  // Filter users: exclude already selected + explicit excludes
  const allExcluded = [...excludeIds, ...selectedIds];
  const filteredUsers = users.filter((u) => {
    if (allExcluded.includes(u.id)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term)
    );
  });

  const displayUsers = filteredUsers.slice(0, 10);
  const hasMore = filteredUsers.length > 10;

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-option]');
      const item = items[highlightedIndex];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleAdd = (userId: string) => {
    onChange([...selectedIds, userId]);
    setSearchTerm('');
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleRemove = (userId: string) => {
    onChange(selectedIds.filter((id) => id !== userId));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) =>
            prev < displayUsers.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < displayUsers.length) {
          handleAdd(displayUsers[highlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'Backspace':
        if (!searchTerm && selectedIds.length > 0) {
          handleRemove(selectedIds[selectedIds.length - 1]);
        }
        break;
    }
  };

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {/* Selected chips + search input */}
      <div
        className={clsx(
          'min-h-[42px] px-2 py-1.5 border border-gray-300 rounded-lg bg-white',
          'flex flex-wrap items-center gap-1.5 cursor-text',
          'focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent'
        )}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        {selectedUsers.map((user) => {
          const config = roleConfig[user.role] || { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'IC' };
          return (
            <span
              key={user.id}
              className={clsx(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs',
                config.color
              )}
            >
              {config.icon} {user.name}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(user.id);
                }}
                className="ml-0.5 hover:text-red-600 font-bold"
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedUsers.length === 0 ? placeholder : 'Add more...'}
          className="flex-1 min-w-[120px] border-none outline-none text-sm py-0.5 bg-transparent"
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div ref={listRef} className="max-h-48 overflow-y-auto">
            {displayUsers.length > 0 ? (
              displayUsers.map((user, idx) => {
                const config = roleConfig[user.role] || { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'IC' };
                return (
                  <button
                    key={user.id}
                    type="button"
                    data-option
                    onClick={() => handleAdd(user.id)}
                    className={clsx(
                      'w-full px-3 py-2 text-left text-sm flex items-center gap-2',
                      highlightedIndex === idx
                        ? 'bg-primary-100 text-primary-900'
                        : 'hover:bg-gray-50'
                    )}
                  >
                    <span className="flex-shrink-0">{config.icon}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 truncate">{user.name}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                    <span className={clsx('text-xs px-2 py-0.5 rounded-full flex-shrink-0', config.color)}>
                      {config.label}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                {searchTerm ? 'No users found' : 'No more users to add'}
              </div>
            )}

            {hasMore && (
              <div className="px-3 py-2 text-center text-xs text-gray-400 border-t border-gray-100">
                {filteredUsers.length - 10} more results. Refine your search.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
